/**
 * Role Management - Club admin interface for managing user roles
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import { Edit, Trash2, User, Shield, Copy, Lock, Key } from 'lucide-react';
import apiService from '../../services/api';
import {
  Button,
  Badge,
  AdminPageLayout,
  StatCard,
  StatGrid,
  EmptyState,
  Modal,
  ConfirmDialog,
  LoadingState,
  FormCheckbox
} from '../../components';
import {
  AttributeStack,
  AttributeGrid
} from '../../components/admin/patterns';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction,
  CopyAction
} from '../../components/listing/v2/ListingAction';
import { ErrorBanner } from '../../components/common';
import { useToast } from '../../contexts/ToastContext';

const RoleManagement = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { } = useRole();
  const [roles, setRoles] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all'
  });
  const useDefaultRoles = true; // Always use default roles logic

  const availablePermissions = [
    { id: 'user_management', name: 'User Management', description: 'Create, edit, and manage club users' },
    { id: 'program_management', name: 'Program Management', description: 'Manage training programs and schedules' },
    { id: 'player_evaluation', name: 'Player Evaluation', description: 'Conduct and view player assessments' },
    { id: 'skills_assessment', name: 'Skills Assessment', description: 'Evaluate player skills and progress' },
    { id: 'document_management', name: 'Document Management', description: 'Manage documents and requirements' },
    { id: 'reports', name: 'Reports', description: 'View club analytics and performance reports' },
    { id: 'settings', name: 'Club Settings', description: 'Configure club preferences and settings' },
    { id: 'registration', name: 'Registration', description: 'Manage player registrations' },
    { id: 'view_child_progress', name: 'View Child Progress', description: 'View progress of registered children' },
    { id: 'program_participation', name: 'Program Participation', description: 'Participate in assigned programs' }
  ];

  const [searchTerm, setSearchTerm] = useState('');

  // Combine roles and templates for display, marking templates
  // Filter out platform admin roles from templates (not applicable for club roles)
  const allRoles = [
    ...roles.map(role => ({ ...role, isTemplate: false })),
    ...roleTemplates
      .filter(template => template.name.toLowerCase() !== 'platform admin')
      .map(template => ({ ...template, isTemplate: true }))
  ];

  const filteredRoles = allRoles.filter(role => {
    // Search filter
    if (filters.search) {
      const searchMatch = role.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        role.description?.toLowerCase().includes(filters.search.toLowerCase());
      if (!searchMatch) return false;
    }
    
    // Type filter
    if (filters.type && filters.type !== 'all') {
      if (filters.type === 'club' && role.isTemplate) return false;
      if (filters.type === 'template' && !role.isTemplate) return false;
    }
    
    return true;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Check if a template has already been copied to club roles
  const isAlreadyCopied = (templateId) => {
    return roles.some(role => role.template_id === templateId);
  };

  useEffect(() => {
    loadRoles();
    loadRoleTemplates();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await apiService.getClubRoles();
      const clubRoles = response.roles || [];

      // If using default roles, also load templates when needed
      if (useDefaultRoles && clubRoles.length === 0) {
        // No roles yet, show templates as available options
        setRoles(clubRoles);
      } else {
        setRoles(clubRoles);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setRoles([]);
      setLoading(false);
    }
  };

  const loadRoleTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await apiService.getRoleTemplates();
      setRoleTemplates(response.templates || []);
      setTemplatesLoading(false);
    } catch (err) {
      console.error('Error loading role templates:', err);
      setRoleTemplates([]);
      setTemplatesLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (selectedRole.usage_count > 0) {
      showError('Cannot delete a role that is currently assigned to users.');
      setShowDeleteModal(false);
      return;
    }

    try {
      await apiService.deleteClubRole(selectedRole.id);
      showSuccess(`Role "${selectedRole.name}" deleted successfully`);
      setShowDeleteModal(false);
      setSelectedRole(null);
      loadRoles();
    } catch (err) {
      showError(`Error deleting role: ${err.message}`);
      setShowDeleteModal(false);
    }
  };

  const handleCopyTemplates = async () => {
    if (selectedTemplates.length === 0) {
      showError('Please select at least one template to copy.');
      return;
    }

    try {
      const result = await apiService.copyRoleTemplates(selectedTemplates);

      if (result.success) {
        showSuccess(`${selectedTemplates.length} template(s) copied successfully`);
        setShowTemplatesModal(false);
        setSelectedTemplates([]);
        loadRoles();
      } else {
        showError(`Error copying templates: ${result.message}`);
      }
    } catch (err) {
      showError(`Error copying templates: ${err.message}`);
    }
  };

  const handleCopySingleTemplate = async (templateId) => {
    try {
      const result = await apiService.copyRoleTemplates([templateId]);

      if (result.success) {
        showSuccess('Template copied successfully');
        loadRoles();
      } else {
        showError(`Error copying template: ${result.message}`);
      }
    } catch (err) {
      showError(`Error copying template: ${err.message}`);
    }
  };

  const handleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const openDeleteModal = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="ROLE MANAGEMENT"
        subtitle="Configure user roles and permissions for your club"
      >
        <LoadingState message="Loading roles..." />
      </AdminPageLayout>
    );
  }

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search roles and templates..."
      filters={[
        {
          name: 'type',
          label: 'Type',
          options: [
            { value: 'all', label: 'All Types' },
            { value: 'club', label: 'Club Roles' },
            { value: 'template', label: 'System Templates' }
          ]
        }
      ]}
      activeFilters={{ type: filters.type || 'all' }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', type: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={() => navigate('/admin/roles/add')} label="New Role" />
      }
    />
  );

  const getRoleIcon = (role) => {
    if (role.isTemplate) return <Shield size={32} />;
    return role.is_staff ? <Lock size={32} /> : <Key size={32} />;
  };

  const getRoleIconVariant = (role) => {
    if (role.isTemplate) return 'secondary';
    return role.is_active !== false ? 'primary' : 'secondary';
  };

  return (
    <AdminPageLayout
      title="ROLE MANAGEMENT"
      subtitle="Configure user roles and permissions for your club"
      backPath="/admin/club"
    >
      {/* Error Banner */}
      {error && (
        <ErrorBanner
          type="error"
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Getting Started */}
      {allRoles.length === 0 && (
        <EmptyState
          icon={<Shield size={48} />}
          title="No Roles Configured Yet"
          description="Create custom roles or copy from templates to get started managing permissions for your club members."
        >
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
            <Button
              onClick={() => navigate('/admin/roles/add')}
              variant="save"
            >
              Add Role
            </Button>
            <Button
              onClick={() => setShowTemplatesModal(true)}
              variant="secondary"
            >
              Copy Templates
            </Button>
          </div>
        </EmptyState>
      )}

      {/* Roles Overview */}
      {allRoles.length > 0 && (
        <>
          <StatGrid>
            <StatCard
              number={allRoles.length}
              label="Total Roles"
            />
            <StatCard
              number={roles.length}
              label="Club Roles"
            />
            <StatCard
              number={roleTemplates.length}
              label="System Templates"
            />
            <StatCard
              number={roles.filter(r => r.is_active !== false).length}
              label="Active Roles"
            />
          </StatGrid>

          <Listing
            loading={loading}
            items={filteredRoles}
            actions={headerActions}
            filters={filterBar}
            renderItem={(role) => (
              <ListingItem
                key={role.id}
                title={role.name}
                subtitle={role.description || 'No description'}
                icon={getRoleIcon(role)}
                iconVariant={getRoleIconVariant(role)}
                actions={
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {role.isTemplate ? (
                      !isAlreadyCopied(role.id) ? (
                        <CopyAction
                          size="xs"
                          onClick={() => handleCopySingleTemplate(role.id)}
                          label="Copy to Club"
                          title="Copy Template to Create Club Role"
                        />
                      ) : (
                        <Badge variant="success" size="sm">COPIED</Badge>
                      )
                    ) : (
                      <>
                        <EditAction
                          size="xs"
                          onClick={() => navigate(`/admin/roles/${role.id}/edit`)}
                        />
                        {role.is_custom && (
                          <DeleteAction
                            size="xs"
                            onClick={() => {
                              setSelectedRole(role);
                              setShowDeleteModal(true);
                            }}
                            disabled={role.usage_count > 0}
                          />
                        )}
                      </>
                    )}
                  </div>
                }
              >
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-light)' }}>
                  <AttributeGrid columns={3} gap="2rem">
                    <AttributeStack
                      label="Permissions"
                      value={role.permissions?.length || 0}
                    />
                    <AttributeStack
                      label="Usage"
                      value={`${role.usage_count || 0} ${role.isTemplate ? 'club' : 'user'}${(role.usage_count || 0) !== 1 ? 's' : ''}`}
                    />
                    <AttributeStack
                      label="Status"
                      value={
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                          {role.isTemplate && (
                            <Badge variant="outline" size="sm">
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Shield size={12} /> SYSTEM TEMPLATE
                              </span>
                            </Badge>
                          )}
                          {role.isTemplate && isAlreadyCopied(role.id) && (
                            <Badge variant="success" size="sm">COPIED</Badge>
                          )}
                          {!role.isTemplate && (
                            <Badge variant="outline" size="sm">
                              {role.is_staff ? 'STAFF' : role.is_custom ? 'CUSTOM' : 'SYSTEM'}
                            </Badge>
                          )}
                          {!role.isTemplate && role.is_active === false && (
                            <Badge variant="warning" size="sm">INACTIVE</Badge>
                          )}
                          {!role.isTemplate && role.is_active !== false && (
                            <Badge variant="success" size="sm">ACTIVE</Badge>
                          )}
                        </div>
                      }
                    />
                  </AttributeGrid>
                </div>
                {role.permissions && role.permissions.length > 0 && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {role.permissions.slice(0, 5).map(permId => (
                      <Badge
                        key={permId}
                        variant="outline"
                        size="sm"
                      >
                        {permId}
                      </Badge>
                    ))}
                    {role.permissions.length > 5 && (
                      <Badge
                        variant="outline"
                        size="sm"
                      >
                        +{role.permissions.length - 5} more
                      </Badge>
                    )}
                  </div>
                )}
              </ListingItem>
            )}
            emptyMessage="No roles found"
          />
        </>
      )}

      {/* Delete Role Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteModal && !!selectedRole}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        message={
          selectedRole?.usage_count > 0
            ? `This role is currently assigned to ${selectedRole.usage_count} user${selectedRole.usage_count !== 1 ? 's' : ''} and cannot be deleted. Please reassign users to different roles before deleting.`
            : `Are you sure you want to delete the role "${selectedRole?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        variant="danger"
        confirmDisabled={selectedRole?.usage_count > 0}
      />

      {/* Copy Templates Modal */}
      <Modal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        title="Copy Role Templates"
        size="sm"
      >
        <p>Select role templates to copy to your club. These will become club-specific roles that you can customize.</p>

        {templatesLoading ? (
          <LoadingState message="Loading templates..." />
        ) : (
          <div className="templates-list">
            {roleTemplates.length === 0 ? (
              <p>No role templates available.</p>
            ) : (
              roleTemplates.map(template => (
                <div key={template.id} className="template-checkbox">
                  <FormCheckbox
                    label=""
                    name={`template-${template.id}`}
                    checked={selectedTemplates.includes(template.id)}
                    onChange={() => {
                      setSelectedTemplates(prev =>
                        prev.includes(template.id)
                          ? prev.filter(id => id !== template.id)
                          : [...prev, template.id]
                      );
                    }}
                  />
                  <div className="template-info">
                    <h4>{template.name}</h4>
                    <p className="template-description">{template.description}</p>
                    <small className="template-usage">
                      Used by {template.usage_count || 0} club{template.usage_count !== 1 ? 's' : ''}
                    </small>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="modal-actions">
          <Button
            onClick={() => setShowTemplatesModal(false)}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopyTemplates}
            variant="save"
            disabled={selectedTemplates.length === 0 || templatesLoading}
          >
            Copy Selected Templates ({selectedTemplates.length})
          </Button>
        </div>
      </Modal>
    </AdminPageLayout>
  );
};

export default RoleManagement;